<template>
  <v-card class="mb-4 pa-2 card-container">
    <v-card-title class="pt-4 card-title">
      <v-container class="pa-0 justify-space-between pr-5" fluid fill-height>
        <span class="pa-1 title">Sessões por Gênero e Faixa Etária</span>
      </v-container>
    </v-card-title>
    <mf-loading loader="faturamento" loader-type="chart">
      <apex-chart
        v-if="series[0].data.length > 0 || series[1].data.length > 0 || series[2].data.length > 0"
        class="pr-5 pl-5"
        height="350"
        :options="chartOptions"
        :series="series"
      />
      <chart-navigator
        v-if="series[0].data.length > 0 || series[1].data.length > 0 || series[2].data.length > 0"
        :selectedPeriod="chartPeriod"
        :chart-index="chartIndex"
        :metrics-length="metricsLength"
        :metrics-period="metricsPeriod"
        @navigateTo="navigateTo"
      />
      <empty-chart v-if="series[0].data.length === 0 && series[1].data.length === 0 && series[2].data.length === 0" />
    </mf-loading>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { QUERY_GET_APP_ACCESSES_BY_GENDER_AND_AGE } from '../../graphql'
import ChartNavigator from '@/components/ChartNavigator'
import EmptyChart from '@/modules/mobile/desempenho/views/components/EmptyChart'
import { colors } from '@/config/colors.js'
import getAppMetricsNavigatorPeriod from '@/services/getAppMetricsNavigatorPeriod'

export default {
  components: {
    ApexChart: VueApexCharts,
    ChartNavigator,
    EmptyChart,
  },
  props: {
    metricsPeriod: String,
  },
  data: () => ({
    chartIndex: 0,
    chartCategories: ['sem dados', '18-24', '25-34', '35-44', '45-54', '55-64', '+65'],
  }),
  computed: {
    series() {
      if (!this.appAccessesByGenderAndAge || this.appAccessesByGenderAndAge.length === 0) {
        return [{ data: [] }, { data: [] }, { data: [] }]
      }
      const currentSelectedMetrics = this.appAccessesByGenderAndAge[this.chartIndex].metrics
      const getAgeRange = (ageRangeNumber) => {
        switch (ageRangeNumber) {
          case '0':
            return 'sem dados'
          case '1':
            return '18-24'
          case '2':
            return '25-34'
          case '3':
            return '35-44'
          case '4':
            return '45-54'
          case '5':
            return '55-64'
          case '6':
            return '+65'
          case '7':
            return null
          case '8':
            return 'sem dados'
        }
      }
      return this.chartCategories.reduce(
        (acc, ageRange) => {
          const [metric] = currentSelectedMetrics.filter((metric) => getAgeRange(metric.age_range) === ageRange)
          if (metric !== undefined) {
            acc[0].data.push(metric.unknown_gender_count)
            acc[1].data.push(metric.male_count)
            acc[2].data.push(metric.female_count)
          } else {
            acc[0].data.push(0)
            acc[1].data.push(0)
            acc[2].data.push(0)
          }
          return acc
        },
        [
          {
            name: 'Outros',
            data: [],
          },
          {
            name: 'Homens',
            data: [],
          },
          {
            name: 'Mulheres',
            data: [],
          },
        ]
      )
    },
    metricsLength() {
      if (!this.appAccessesByGenderAndAge) return 0
      return this.appAccessesByGenderAndAge.length - 1
    },
    chartPeriod() {
      if (!this.appAccessesByGenderAndAge || this.appAccessesByGenderAndAge.length === 0) return
      const currentPeriod =
        this.metricsPeriod === 'MONTHLY' ? this.appAccessesByGenderAndAge[this.chartIndex].month : this.appAccessesByGenderAndAge[this.chartIndex].year
      const { navigatorPeriod } = getAppMetricsNavigatorPeriod(this.metricsPeriod, currentPeriod)
      return navigatorPeriod
    },
    chartOptions() {
      return {
        chart: {
          height: 350,
          type: 'bar',
          stacked: true,
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
          background: '#fff',
        },
        fill: {
          opacity: 1,
          type: 'solid',
        },
        plotOptions: {
          bar: {
            columnWidth: '20px',
            borderRadius: 3,
            dataLabels: {
              position: 'top',
            },
          },
        },
        grid: {
          padding: {
            top: 10,
            right: 20,
            bottom: 10,
            left: 40,
          },
        },
        colors: ['#969EA7', colors[0], colors[1]],
        legend: {
          horizontalAlign: 'center',
          position: 'right',
          height: 200,
          width: 200,
          markers: {
            width: 7,
            height: 7,
            radius: 5,
          },
          shape: 'circle',
          offsetX: -50,
          offsetY: 70,
          formatter: function (seriesName, opts) {
            const totalSessions = opts.w.globals.series.map((item) => item.reduce((acc, item) => acc + item)).reduce((acc, item) => item + acc)
            const seriesTotal = opts.w.globals.series[opts.seriesIndex].reduce((acc, item) => acc + item)
            const seriesNameLegend = `<span class="pr-1 ml-2 cinza-l30-text" style="font-size: 14px; margin-bottom: 5px">${seriesName}</span>`
            const seriesTotalLegend = `<br> <span class="cinza-d20-text" style="font-weight: 500; font-size: 18px">${seriesTotal.toLocaleString()}</span>`
            const seriesPercentLegend = `<span class="ml-3 cinza-l30-text" style="font-size: 18px; margin-bottom: 10px">${((seriesTotal / totalSessions) * 100)
              .toFixed(2)
              .replace('.', ',')}%</span>`
            return `${seriesNameLegend}${seriesTotalLegend}${seriesPercentLegend}<br><br>`
          },
          onItemClick: {
            toggleDataSeries: false,
          },
        },
        markers: {
          size: 2,
          colors: [colors[0], colors[1], '#969EA7'],
          strokeColors: [colors[0], colors[1], '#969EA7'],
          strokeWidth: 2,
          strokeOpacity: 1,
          strokeDashArray: 0,
          fillOpacity: 1,
          shape: 'circle',
          radius: 2,
          showNullDataPoints: true,
          hover: {
            sizeOffset: 2,
          },
        },
        tooltip: {
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const maleCount = series[1][dataPointIndex]
            const femaleCount = series[2][dataPointIndex]
            const unknownCount = series[0][dataPointIndex]
            const seriesName = this.chartCategories[dataPointIndex]
            return `
              <v-card class="pr-5 pl-5 pt-2 pb-2">
                <span style="color: ${colors[0]};font-weight: bold">${maleCount.toLocaleString()}</span>
                <span class="cinza-d20-text"> Homens</span>
                </br>
                <span style="color: ${colors[1]};font-weight: bold">${femaleCount.toLocaleString()}</span>
                <span class="cinza-d20-text"> Mulheres</span>
                </br>
                <span class="cinza-l30-text" style="font-weight: bold">${unknownCount.toLocaleString()}</span>
                <span class="cinza-d20-text"> Outros</span>
                ${
                  seriesName === 'sem dados'
                    ? ''
                    : `
                  </br>
                  <span class="cinza-d20-text">Dentro da faixa etária ${seriesName}</span>`
                }
              </v-card>
            `
          },
        },
        dataLabels: {
          enabled: true,
          enabledOnSeries: [1, 2],
          textAnchor: 'middle',
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ['#F0F1F2'],
            fontWeight: 'normal',
          },
          background: {
            enabled: true,
            foreColor: '#969EA7',
            padding: 4,
            borderRadius: 2,
          },
          formatter: function (
            value,
            {
              seriesIndex,
              dataPointIndex,
              w: {
                globals: { series },
              },
            }
          ) {
            const maleCount = series[0][dataPointIndex]
            const femaleCount = series[1][dataPointIndex]
            const unknowGenderCount = series[2][dataPointIndex]
            const totalCount = maleCount + femaleCount + unknowGenderCount
            let formattedValue = totalCount > 1000 ? `${(totalCount / 1000).toFixed(1).toLocaleString()}k` : totalCount
            // lib está com um bug, não aparece o label em gráficos empilhados quando a série de cima é zero, deixei as labels nas duas colunas e escondi na primeira quando a segunda não for 0
            if (series[2][dataPointIndex] !== 0 && seriesIndex === 1) formattedValue = ''
            return formattedValue
          },
        },
        xaxis: {
          categories: this.chartCategories,
          labels: {
            style: {
              colors: '#969EA7',
            },
          },
        },
        yaxis: {
          labels: {
            formatter: function (val, index) {
              return val.toLocaleString()
            },
          },
        },
      }
    },
  },
  apollo: {
    appAccessesByGenderAndAge: {
      query: QUERY_GET_APP_ACCESSES_BY_GENDER_AND_AGE,
      variables() {
        return { metricsPeriod: this.metricsPeriod }
      },
    },
  },
  watch: {
    metricsPeriod() {
      this.chartIndex = 0
    },
  },
  methods: {
    navigateTo(newIndex) {
      this.chartIndex = newIndex
    },
  },
}
</script>

<style lang="scss" scoped>
.card-container {
  min-height: 400px;
}
.local-subtitle {
  width: 80%;
}
.xaxis-title,
::v-deep .cinza-l30-text {
  color: $cinza-l30;
}
::v-deep .cinza-d20-text {
  color: $cinza-d20;
}
</style>
